import { useEffect, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FloatInput, FloatSelect, showToast } from "src/componentes";
import { CTeFiltrar } from "src/services/Fiscal.Service";
import {
  CTeDto,
  finalidadeCteOptions,
  FINALIDADE_CTE_COMPLEMENTAR,
  EStatusEnvioCte,
  getLocal,
  isCteComplementoValores,
  modalOptions,
  retiraOptions,
  STORAGECTE_KEY,
  tipoCteOptions,
  tipoServicoOptions,
  tipoTomadorOptions,
  updateLocal,
  validarChaveCTe,
} from "../CTe.Dto";
import {
  CaracteristicasFormType,
  CaracteristicasSchema,
} from "./CTe.Cadastro.Dto";
import CTeConfiguracaoCode from "../CTe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "src/pages/Privada/Configuracoes/Sistema/Sistema.Dto";

const CTeCadastroCaracteristicasView = () => {
  const navigate = useNavigate();
  const configuracao = CTeConfiguracaoCode();
  const cte = getLocal<CTeDto>(STORAGECTE_KEY);

  const form = useForm<CaracteristicasFormType>({
    resolver: yupResolver(CaracteristicasSchema),
    defaultValues: {
      tipoCte: cte?.tipoCte ?? 0,
      chaveCTeComplementado: cte?.chaveCTeComplementado ?? "",
      tipoServico: cte?.tipoServico ?? 0,
      finalidadeCte: cte?.finalidadeCte ?? 0,
      modal: cte?.modal ?? 1,
      retira: cte?.retira ?? 1,
      tipoTomador: cte?.tipoTomador ?? 3,
      rntrc:
        cte?.rntrc ||
        configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.RNTRC) ||
        "",
    },
  });

  const tipoCteSelecionado = form.watch("tipoCte");
  const isComplemento = isCteComplementoValores(tipoCteSelecionado);

  // Complemento de valores: finalidade é sempre "Complementar"
  useEffect(() => {
    if (isComplemento) {
      form.setValue("finalidadeCte", FINALIDADE_CTE_COMPLEMENTAR);
      form.clearErrors("rntrc");
    } else if (form.getValues("finalidadeCte") === FINALIDADE_CTE_COMPLEMENTAR) {
      form.setValue("finalidadeCte", 0);
    }
  }, [isComplemento]);

  // Pré-preenche o CT-e complementar com os dados do CT-e original (quando existe na base)
  const chaveDigitada = form.watch("chaveCTeComplementado");
  const ultimaChaveBuscada = useRef<string | null>(
    cte?.chaveCTeComplementado ?? null
  );

  useEffect(() => {
    const chave = (chaveDigitada ?? "").trim();
    if (
      !isComplemento ||
      validarChaveCTe(chave) !== null ||
      ultimaChaveBuscada.current === chave
    ) {
      return;
    }
    ultimaChaveBuscada.current = chave;

    (async () => {
      try {
        const response = await CTeFiltrar({ chaveAcesso: chave });
        if (!response.data.success) {
          return;
        }
        const original: CTeDto | undefined = response.data.data[0];
        if (!original) {
          return;
        }

        const status = original.autorizacao?.codigoStatusEnvio;
        if (
          Number(original.tipoCte) !== 0 ||
          (status !== undefined &&
            status !== null &&
            Number(status) !== EStatusEnvioCte.Finalizada)
        ) {
          showToast({
            message:
              "O CT-e informado deve ser um CT-e normal, autorizado e não cancelado/denegado.",
            title: "CT-e complementado",
            variant: "Warning",
          });
          return;
        }

        updateLocal<CTeDto>(STORAGECTE_KEY, {
          tributacaoId: original.tributacaoId,
          tributacaoNome: original.tributacaoNome,
          codigoMunicipioInicio: original.codigoMunicipioInicio,
          nomeMunicipioInicio: original.nomeMunicipioInicio,
          ufInicio: original.ufInicio,
          codigoMunicipioFim: original.codigoMunicipioFim,
          nomeMunicipioFim: original.nomeMunicipioFim,
          ufFim: original.ufFim,
          remetente: original.remetente,
          destinatario: original.destinatario,
          tomador: original.tomador,
        });
        form.setValue("tipoServico", Number(original.tipoServico));
        form.setValue("modal", Number(original.modal));
        form.setValue("retira", Number(original.retira));
        form.setValue("tipoTomador", Number(original.tipoTomador));

        showToast({
          message:
            "Dados do CT-e original importados (percurso, tributação, partes e tomador). Revise nas próximas etapas.",
          title: "CT-e complementado",
          variant: "Info",
        });
      } catch (error) {
        console.error("Erro ao buscar CT-e complementado:", error);
      }
    })();
  }, [chaveDigitada, isComplemento]);

  // Atribui o RNTRC automaticamente com base na configuração carregada caso ainda não definido
  useEffect(() => {
    if (!configuracao.loading) {
      const rntrcConfig = configuracao.getConfiguracaoValor(
        TipoConfiguracaoEnum.RNTRC
      );
      if (rntrcConfig) {
        const cteAtual = getLocal<CTeDto>(STORAGECTE_KEY);
        if (!cteAtual?.rntrc && !form.getValues("rntrc")) {
          form.setValue("rntrc", rntrcConfig);
        }
      }
    }
  }, [configuracao.loading]);

  const handleVoltar = () => {
    navigate("/fiscal/cte");
  };

  const onSubmit = (data: CaracteristicasFormType) => {
    const serieConfig = Number(
      configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.Serie)
    );

    const complemento = isCteComplementoValores(data.tipoCte);

    updateLocal<CTeDto>(STORAGECTE_KEY, {
      tipoCte: Number(data.tipoCte),
      chaveCTeComplementado: complemento
        ? (data.chaveCTeComplementado ?? "").trim()
        : undefined,
      // Complemento não possui carga, documentos nem RNTRC
      ...(complemento ? { carga: undefined, documentos: [] } : {}),
      tipoServico: Number(data.tipoServico),
      finalidadeCte: complemento
        ? FINALIDADE_CTE_COMPLEMENTAR
        : Number(data.finalidadeCte),
      modal: Number(data.modal),
      retira: Number(data.retira),
      tipoTomador: Number(data.tipoTomador),
      rntrc: complemento ? undefined : (data.rntrc ?? ""),
      serie: cte?.serie || (serieConfig > 0 ? serieConfig : 1),
      numeroCte: cte?.numeroCte ?? 0,
    });

    navigate("../percurso");
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit)} className="mt-3 mb-5">
      <Row className="g-3">
        <Col xs={12} md={6}>
          <FloatSelect
            label="Tipo do CT-e"
            options={tipoCteOptions}
            register={form.register("tipoCte", { valueAsNumber: true })}
            errorsMessage={form.formState.errors.tipoCte}
          />
        </Col>

        <Col xs={12} md={6}>
          <FloatSelect
            label="Tipo de Serviço"
            options={tipoServicoOptions}
            register={form.register("tipoServico", { valueAsNumber: true })}
            errorsMessage={form.formState.errors.tipoServico}
          />
        </Col>

        <Col xs={12} md={4}>
          <FloatSelect
            label="Finalidade"
            options={finalidadeCteOptions.filter(
              (o) => (o.value === FINALIDADE_CTE_COMPLEMENTAR) === isComplemento
            )}
            register={form.register("finalidadeCte", { valueAsNumber: true })}
            errorsMessage={form.formState.errors.finalidadeCte}
          />
        </Col>

        <Col xs={12} md={4}>
          <FloatSelect
            label="Modal"
            options={modalOptions}
            register={form.register("modal", { valueAsNumber: true })}
            errorsMessage={form.formState.errors.modal}
          />
        </Col>

        <Col xs={12} md={4}>
          <FloatSelect
            label="Recebimento / Retira"
            options={retiraOptions}
            register={form.register("retira", { valueAsNumber: true })}
            errorsMessage={form.formState.errors.retira}
          />
        </Col>

        <Col xs={12} md={6}>
          <FloatSelect
            label="Tomador do Serviço"
            options={tipoTomadorOptions}
            register={form.register("tipoTomador", { valueAsNumber: true })}
            errorsMessage={form.formState.errors.tipoTomador}
          />
        </Col>

        {isComplemento ? (
          <Col xs={12} key="chave-cte-complementado">
            <FloatInput
              label="Chave de Acesso do CT-e Complementado"
              registerField={form.register("chaveCTeComplementado")}
              errorsMessage={form.formState.errors.chaveCTeComplementado}
              inputMask="int"
              maxLength={44}
            />
          </Col>
        ) : (
          <Col xs={12} md={6} key="rntrc">
            <FloatInput
              label="RNTRC"
              registerField={form.register("rntrc")}
              errorsMessage={form.formState.errors.rntrc}
              inputMask="int"
            />
          </Col>
        )}
      </Row>

      <div className="fixed-bottom">
        <Container className="py-3">
          <Row>
            <Col xs={6} className="d-grid">
              <Button variant="outline-secondary" onClick={handleVoltar}>
                Voltar
              </Button>
            </Col>
            <Col xs={6} className="d-grid">
              <Button variant="outline-primary" type="submit">
                Próximo
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </Form>
  );
};

export default CTeCadastroCaracteristicasView;
